import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { selectEmail, selectPhone, clearRegistrationData } from '../store/slices/registrationSlice';
import Layout from '../components/Layout';
import authService from '../services/auth';
import './Auth.css';

const VerifyPhone = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const email = useAppSelector(selectEmail);
  const phone = useAppSelector(selectPhone);
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [resendTimer, setResendTimer] = useState(60);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    // Redirect to signup if no registration data
    if (!email || !phone) {
      navigate('/signup');
    }

    // Countdown timer
    const timer = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate]);

  const handleCodeChange = (index, value) => {
    if (value.length > 1) return;
    
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      document.getElementById(`code-${index + 1}`)?.focus();
    }

    setError('');
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      document.getElementById(`code-${index - 1}`)?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    if (pastedData.length === 6 && /^\d+$/.test(pastedData)) {
      const newCode = pastedData.split('');
      setCode(newCode);
      document.getElementById('code-5')?.focus();
    }
  };

  const handleVerify = async () => {
    const verificationCode = code.join('');
    
    if (verificationCode.length !== 6) {
      setError('Please enter the complete verification code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await authService.verifyPhone({
        email,
        phone,
        code: verificationCode
      });

      // Clear registration data from Redux
      dispatch(clearRegistrationData());
      
      // If token is provided, user is logged in
      if (response.data.token) {
        // Navigate to home
        navigate('/', { state: { message: 'Account created successfully! You are now logged in.' } });
      } else {
        // Navigate to login
        navigate('/login', { state: { message: 'Phone verified successfully! Please login.' } });
      }
    } catch (error) {
      console.error('Verification error:', error);
      
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        setError(errorData.message || errorData.errors?.code?.[0] || 'Invalid verification code');
      } else {
        setError(error.message || 'Verification failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setError('');

    try {
      await authService.resendPhoneCode({ email, phone });
      setResendTimer(60);
      setError(''); // Clear any previous errors
      // Show success message (you can add a success state if needed)
    } catch (error) {
      console.error('Resend error:', error);
      
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        if (errorData.rate_limited) {
          setResendTimer(errorData.remaining_seconds || 60);
        }
        setError(errorData.message || 'Failed to resend code. Please try again.');
      } else {
        setError(error.message || 'Failed to resend code. Please try again.');
      }
    } finally {
      setResending(false);
    }
  };

  return (
    <Layout>
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-card">
            <h1>Verify Your Phone</h1>
            <p className="auth-subtitle">
              We've sent a verification code to <strong>{phone}</strong>
            </p>

            <div className="verification-code-container">
              {code.map((digit, index) => (
                <input
                  key={index}
                  id={`code-${index}`}
                  type="text"
                  inputMode="numeric"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  className="code-input"
                />
              ))}
            </div>

            {error && <span className="error-message">{error}</span>}

            <button onClick={handleVerify} className="auth-btn" disabled={loading}>
              {loading ? 'Verifying...' : 'Verify Phone'}
            </button>

            <div className="resend-section">
              <p>Didn't receive the code?</p>
              {resendTimer > 0 ? (
                <p className="resend-timer">Resend in {resendTimer}s</p>
              ) : (
                <button onClick={handleResend} className="resend-btn" disabled={resending}>
                  {resending ? 'Sending...' : 'Resend Code'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default VerifyPhone;
